/**
 * Suggest Change Modal Component
 * 
 * Allows technicians to propose template improvements
 */

import { useState } from 'react';
import { Lightbulb, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { InspectionTemplateItem } from '@/hooks/useInspectionTemplate';

interface SuggestChangeModalProps {
  shopId: string;
  templateKey: string;
  templateItems: InspectionTemplateItem[];
  trigger?: React.ReactNode;
}

type ChangeScope = 'ITEM' | 'SYSTEM';
type Operation = 'add_item' | 'update_item';

export function SuggestChangeModal({
  shopId,
  templateKey,
  templateItems,
  trigger,
}: SuggestChangeModalProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scope, setScope] = useState<ChangeScope>('ITEM');
  const [selectedItemKey, setSelectedItemKey] = useState<string>('');
  const [operation, setOperation] = useState<Operation>('update_item');
  const [reason, setReason] = useState('');
  
  // Proposed change fields
  const [proposedName, setProposedName] = useState('');
  const [proposedDescription, setProposedDescription] = useState('');
  const [proposedSystemKey, setProposedSystemKey] = useState('');
  
  const { toast } = useToast();

  const resetForm = () => {
    setScope('ITEM');
    setSelectedItemKey('');
    setOperation('update_item');
    setReason('');
    setProposedName('');
    setProposedDescription('');
    setProposedSystemKey('');
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast({
        title: 'Reason required',
        description: 'Please provide a reason for your suggestion.',
        variant: 'destructive',
      });
      return;
    }

    if (scope === 'ITEM' && operation === 'update_item' && !selectedItemKey) {
      toast({
        title: 'Item required',
        description: 'Please select an item to update.',
        variant: 'destructive',
      });
      return;
    }

    if (operation === 'add_item' && !proposedName.trim()) {
      toast({
        title: 'Name required',
        description: 'Please provide a name for the new item.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build proposed patch
      const proposedPatch: Record<string, unknown> = {};
      
      if (operation === 'add_item') {
        proposedPatch.op = 'add_item';
        proposedPatch.name = proposedName;
        proposedPatch.description = proposedDescription || null;
        if (proposedSystemKey) {
          proposedPatch.system_key = proposedSystemKey;
        }
      } else {
        proposedPatch.op = 'update_item';
        if (proposedName) proposedPatch.name = proposedName;
        if (proposedDescription) proposedPatch.description = proposedDescription;
      }

      const { error } = await (supabase
        .from('inspection_template_change_requests') as any)
        .insert({
          shop_id: shopId,
          template_key: templateKey,
          change_scope: scope,
          item_key: scope === 'ITEM' ? selectedItemKey : null,
          system_key: proposedSystemKey || null,
          proposed_patch: proposedPatch,
          reason: reason.trim(),
          status: 'PENDING',
        });

      if (error) throw error;

      toast({
        title: 'Suggestion submitted',
        description: 'Your improvement suggestion has been submitted for review.',
      });

      setOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
      toast({
        title: 'Submission failed',
        description: error instanceof Error ? error.message : 'Failed to submit suggestion.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get unique system keys from items
  const systemKeys = [...new Set(templateItems.map(i => i.system_key))];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Lightbulb className="h-4 w-4" />
            Suggest Improvement
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Suggest Inspection Improvement</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Template key (read-only) */}
          <div className="space-y-2">
            <Label>Template</Label>
            <Input value={templateKey} disabled />
          </div>

          {/* Scope selection */}
          <div className="space-y-2">
            <Label>What would you like to change?</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as ChangeScope)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ITEM">Inspection Item</SelectItem>
                <SelectItem value="SYSTEM">System/Category</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Operation selection */}
          <div className="space-y-2">
            <Label>Type of change</Label>
            <Select value={operation} onValueChange={(v) => setOperation(v as Operation)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="update_item">Update existing</SelectItem>
                <SelectItem value="add_item">Add new</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Item selection (for updates) */}
          {scope === 'ITEM' && operation === 'update_item' && (
            <div className="space-y-2">
              <Label>Select item to update</Label>
              <Select value={selectedItemKey} onValueChange={setSelectedItemKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an item..." />
                </SelectTrigger>
                <SelectContent>
                  {templateItems.map(item => (
                    <SelectItem key={item.item_key} value={item.item_key}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* System selection (for add_item) */}
          {operation === 'add_item' && (
            <div className="space-y-2">
              <Label>System/Category</Label>
              <Select value={proposedSystemKey} onValueChange={setProposedSystemKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a system..." />
                </SelectTrigger>
                <SelectContent>
                  {systemKeys.map(key => (
                    <SelectItem key={key} value={key}>
                      {key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Proposed name */}
          <div className="space-y-2">
            <Label>{operation === 'add_item' ? 'Item Name' : 'New Name (optional)'}</Label>
            <Input
              value={proposedName}
              onChange={(e) => setProposedName(e.target.value)}
              placeholder={operation === 'add_item' ? 'Enter item name' : 'Leave blank to keep current'}
            />
          </div>

          {/* Proposed description */}
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input
              value={proposedDescription}
              onChange={(e) => setProposedDescription(e.target.value)}
              placeholder="Brief description..."
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason for change *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this change would improve the inspection..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              'Submit Suggestion'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
